import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api } from '@/lib/api';
import { displayGroupName } from '@/lib/group-display';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

interface GroupAffiliation {
  id: string;
  name: string;
  type: 'manual' | 'template';
  isLeader: boolean;
  roles: string[];
}

interface MemberInvolvement {
  groups: GroupAffiliation[];
  committees: Array<{
    id: string;
    name: string;
    isLeader: boolean;
    roles: string[];
  }>;
  departments: Array<{
    id: string;
    name: string;
    roles: string[];
  }>;
  units: Array<{
    id: string;
    name: string;
    departmentId: string;
    departmentName: string;
    roles: string[];
  }>;
}

const uniqueRoles = (roles: string[]) => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const role of roles) {
    const key = role.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(role);
  }
  return result;
};

const InvolvementListRow = ({
  label,
  items,
}: {
  label: string;
  items: Array<{ key: string; to?: string; name: string; roles: string[] }>;
}) => (
  <div className="grid grid-cols-1 gap-0.5 text-body-sm sm:grid-cols-[8.5rem_1fr] sm:gap-2">
    <dt className="text-fg-secondary">{label}</dt>
    <dd className="min-w-0">
      <ul className="flex flex-col gap-1">
        {items.map((item) => (
          <li key={item.key} className="min-w-0 break-words">
            {item.to ? (
              <Link to={item.to} className="font-medium text-brand hover:underline">
                {item.name}
              </Link>
            ) : (
              <span className="font-medium">{item.name}</span>
            )}
            {item.roles.length > 0 ? (
              <span className="text-fg-secondary"> · {item.roles.join(', ')}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </dd>
  </div>
);

export const MemberQuickViewInvolvement = ({ memberId }: { memberId: string }) => {
  const { t } = useTranslation('people');
  const { t: tGroups } = useTranslation('groups');
  const churchId = useChurchStore((state) => state.church?.id ?? null);
  const membership = useChurchStore((state) => state.membership);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const canViewGroups =
    isModuleEnabled('groups') && hasPermission(membership, PERMISSIONS.GROUPS_VIEW);

  const involvement = useQuery({
    queryKey: ['people', 'member', churchId, memberId, 'involvement'],
    queryFn: () => api.get<MemberInvolvement>(`/people/members/${memberId}/involvement`),
    enabled: Boolean(churchId && memberId),
  });

  if (involvement.isLoading || involvement.isError || !involvement.data) {
    return null;
  }

  const data = involvement.data;
  const toGroupItem = (row: GroupAffiliation) => ({
    key: row.id,
    to: canViewGroups ? `/groups/${row.id}` : undefined,
    name: displayGroupName(row),
    roles: uniqueRoles([...(row.isLeader ? [tGroups('leader')] : []), ...row.roles]),
  });
  const manualGroupItems = data.groups.filter((row) => row.type === 'manual').map(toGroupItem);
  const smartGroupItems = data.groups.filter((row) => row.type === 'template').map(toGroupItem);
  const committeeItems = data.committees.map((row) => ({
    key: row.id,
    to: canViewGroups ? `/committees/${row.id}` : undefined,
    name: row.name,
    roles: uniqueRoles([...(row.isLeader ? [tGroups('leader')] : []), ...row.roles]),
  }));
  const departmentItems = data.departments.map((row) => ({
    key: row.id,
    to: `/people/departments/${row.id}`,
    name: row.name,
    roles: uniqueRoles(row.roles),
  }));
  const unitItems = data.units.map((row) => ({
    key: row.id,
    to: `/people/departments/${row.departmentId}`,
    name: t('members.detail.involvement.unitInDepartment', {
      unit: row.name,
      department: row.departmentName,
    }),
    roles: row.roles,
  }));

  const showManualGroups = isModuleEnabled('groups') && manualGroupItems.length > 0;
  const showSmartGroups = isModuleEnabled('groups') && smartGroupItems.length > 0;
  const showCommittees = isModuleEnabled('groups') && committeeItems.length > 0;
  const showDepartments = departmentItems.length > 0;
  const showUnits = unitItems.length > 0;

  if (!showManualGroups && !showSmartGroups && !showCommittees && !showDepartments && !showUnits) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-body-sm font-semibold text-fg">{t('members.detail.sections.involvement')}</h3>
      <dl className="space-y-3">
        {showManualGroups ? (
          <InvolvementListRow
            label={t('members.detail.involvement.manualGroups')}
            items={manualGroupItems}
          />
        ) : null}
        {showSmartGroups ? (
          <InvolvementListRow
            label={t('members.detail.involvement.smartGroups')}
            items={smartGroupItems}
          />
        ) : null}
        {showCommittees ? (
          <InvolvementListRow label={t('members.detail.involvement.committees')} items={committeeItems} />
        ) : null}
        {showDepartments ? (
          <InvolvementListRow
            label={t('members.detail.involvement.departments')}
            items={departmentItems}
          />
        ) : null}
        {showUnits ? (
          <InvolvementListRow label={t('members.detail.involvement.units')} items={unitItems} />
        ) : null}
      </dl>
    </div>
  );
};
